import { useMemo } from 'react';

import {
  ColDef,
  DateFilterParams,
  ICellRendererParams,
  SideBarDef,
  TextFilterParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { format, isValid } from 'date-fns';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useFeatureFlag } from '@/app/services/features';
import { setQuickFilter } from '@/app/slices/quickFilter';
import { CustomViews } from '@/components/common/custom-views';
import { DonorFertilizationLink } from '@/components/common/links';
import { CSS_CERTIFIED, EXPORT_ENUMS, SEX } from '@/constants';
import { cssCertifiedFormatter } from '@/grid/value-formatters/cssCertifiedFormatter';

const formatSex = (
  sex?: number | string | null,
  useConventionalLabel = false,
) => {
  const numericSex = typeof sex === 'string' ? Number(sex) : sex;

  if (numericSex === SEX.Female) return 'Female';
  if (numericSex === SEX.Male) return 'Male';
  if (numericSex === SEX.Unknown) {
    return useConventionalLabel ? 'Conventional' : 'Unknown';
  }
  return '';
};

const formatDateMonthDayYear = (params: ValueFormatterParams): string => {
  if (!params.value) return '';
  const date = new Date(params.value);
  if (!isValid(date)) return '';
  return format(date, 'MM-dd-yyyy');
};

const semenDataPointColumnDefs: ColDef[] = [
  {
    field: 'units',
    headerName: 'Units',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'sire.name',
    headerName: 'Sire',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'semenType',
    headerName: 'Semen Type',
    width: 140,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'cssCertified',
    headerName: 'CSS Stamped',
    width: 150,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [CSS_CERTIFIED.No, CSS_CERTIFIED.Yes],
      valueFormatter: (params: any) => {
        return params.value === CSS_CERTIFIED.No
          ? 'No'
          : params.value === CSS_CERTIFIED.Yes
            ? 'Yes'
            : 'Unknown';
      },
    },
    valueFormatter: cssCertifiedFormatter,
    wrapHeaderText: true,
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'createdOn',
    headerName: 'Created On',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'overriddenCreatedOn',
    headerName: 'Record Created On',
    width: 160,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'modifiedBy',
    headerName: 'Modified By',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'modifiedOn',
    headerName: 'Modified On',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'soldTo',
    headerName: 'Sold To',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'storageStartDate',
    headerName: 'Storage Start Date',
    width: 150,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'togId',
    headerName: 'TogId',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
];

/**
 * Applies the Semen_Type_Field_Label flag behavior to a static column-defs
 * array: drops the "semenType" column and relabels the "sex" column's
 * headerName to "Semen Type" when the flag is enabled; returns the columns
 * unchanged when it's disabled.
 */
export const applySemenTypeLabelToColumnDefs = (
  columnDefs: ColDef[],
  isSemenTypeLabelEnabled: boolean,
  isSemenConventionalTypeEnabled = false,
): ColDef[] =>
  isSemenTypeLabelEnabled
    ? columnDefs
        .filter((col) => col.field !== 'semenType')
        .map((col) =>
          col.field === 'sex'
            ? {
                ...col,
                headerName: 'Semen Type',
                filterParams: {
                  ...(col.filterParams ?? {}),
                  valueFormatter: (params: any) =>
                    formatSex(params.value, isSemenConventionalTypeEnabled),
                },
                valueFormatter: (params: ValueFormatterParams) =>
                  formatSex(params.value, isSemenConventionalTypeEnabled),
              }
            : col,
        )
    : columnDefs;

export const useHomeTabSemenColumnDefs = () => {
  const isSemenDataPointsEnabled = useFeatureFlag('Semen_Data_Points');
  const isAddStorageLocationSiteEnabled = useFeatureFlag(
    'Add_Storage_Location_Site',
  );
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );

  return useMemo(
    () =>
      [
        {
          headerName: '',
          width: 50,
          checkboxSelection: true,
          suppressMovable: true,
          lockPosition: true,
        },
        {
          field: 'numberOfStrawsToAdd',
          headerName: '# to add',
          width: 80,
          filter: false,
          wrapHeaderText: true,
          editable: true,
          valueParser: (params) => {
            const val = Number(params.newValue);
            return Number.isNaN(val) ? null : val;
          },
          cellClass:
            'bg-white rounded mx-0.5 max-w-12 h-[1.6rem] flex items-center justify-center semen-straws-to-add-cell',
          cellStyle: {
            borderRadius: '4px',
            margin: 'auto 2px',
            maxWidth: '3rem',
            height: '1.6rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          width: 150,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
        },

        {
          field: 'cane',
          headerName: 'Cane Code',
          width: 120,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
          filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        },
        {
          field: 'colCode',
          headerName: 'Collection Code',
          width: 140,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
        },
        {
          field: 'stateCode',
          headerName: 'Active',
          width: 100,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [0, 1],
            valueFormatter: (params: any) => {
              return params.value === 0 ? 'Yes' : 'No';
            },
            buttons: ['apply', 'clear'],
            closeOnApply: true,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === 0 ? 'Yes' : 'No';
          },
        },
        {
          field: 'quantity',
          headerName: 'Quantity',
          width: 100,
          filter: 'agNumberColumnFilter',
          wrapHeaderText: true,
        },
        ...(!isSemenTypeLabelEnabled
          ? ([
              {
                field: 'sexed',
                headerName: 'Sexed',
                width: 80,
                filter: 'agSetColumnFilter',
                filterParams: {
                  values: [true, false],
                  valueFormatter: (params: any) => {
                    return params.value === true ? 'Yes' : 'No';
                  },
                },
                valueFormatter: (params: ValueFormatterParams) => {
                  return params.value === true ? 'Yes' : 'No';
                },
                wrapHeaderText: true,
              },
            ] as ColDef[])
          : []),
        {
          field: 'sex',
          headerName: isSemenTypeLabelEnabled ? 'Semen Type' : 'Sex',
          width: 80,
          wrapHeaderText: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [1, 2, 3],
            valueFormatter: (params: any) => {
              return formatSex(params.value, isSemenConventionalTypeEnabled);
            },
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return formatSex(params.value, isSemenConventionalTypeEnabled);
          },
        },
        {
          field: 'ownerName',
          headerName: 'Owner Name',
          width: 150,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
        },
        {
          field: 'ownerCode',
          headerName: 'Ranch ID',
          width: 140,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
          filterParams: {
            buttons: ['apply', 'clear'],
            closeOnApply: true,
            filterOptions: ['contains'], // limited to match quick filter
          } as TextFilterParams,
        },
        {
          field: 'storageLocation.name',
          headerName: 'Semen Location',
          width: 140,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
          filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        },
        {
          field: 'dateReceived',
          headerName: 'Date Received',
          width: 140,
          filter: 'agDateColumnFilter',
          wrapHeaderText: true,
          filterParams: {
            buttons: ['apply', 'clear'],
            closeOnApply: true,
            filterOptions: ['equals'], // limited to match quick filter
          } as DateFilterParams,
          valueFormatter: formatDateMonthDayYear,
        },
        {
          field: 'naab',
          headerName: 'NAAB Code',
          width: 140,
          filter: 'agTextColumnFilter',
          filterParams: {
            buttons: ['apply', 'clear'],
            closeOnApply: true,
            filterOptions: ['contains'], // limited to match quick filter
          } as TextFilterParams,
          wrapHeaderText: true,
        },
        {
          field: 'sire.breed.name',
          headerName: 'Breed',
          width: 100,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
          filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        },
        {
          field: 'donorScheduleDate',
          headerName: 'Donor Schedule Date',
          width: 140,
          filter: 'agDateColumnFilter',
          wrapHeaderText: true,
          filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
          valueFormatter: formatDateMonthDayYear,
          hide: true,
        },
        {
          field: 'stateCode',
          headerName: 'Semen Status',
          width: 130,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [0, 1],
            valueFormatter: (params: any) => {
              return params.value === 0 ? 'Active' : 'Inactive';
            },
            buttons: ['apply', 'clear'],
            closeOnApply: true,
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === 0 ? 'Active' : 'Inactive';
          },
          wrapHeaderText: true,
          hide: true,
        },
        {
          field: 'shipmentNumber',
          headerName: 'Shipment Number',
          width: 150,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
          filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
        },
        {
          field: 'quantityReceived',
          headerName: 'Quantity Received',
          width: 100,
          filter: 'agNumberColumnFilter',
          wrapHeaderText: true,
        },
        {
          field: 'quantityUsed',
          headerName: 'Quantity Used',
          width: 100,
          filter: 'agNumberColumnFilter',
          wrapHeaderText: true,
          hide: true,
        },
        {
          field: 'export',
          headerName: 'Export',
          width: 150,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: Object.values(EXPORT_ENUMS),
            valueFormatter: (params: any) => {
              return (
                Object.entries(EXPORT_ENUMS).find(
                  ([, val]) => val === params.value,
                )?.[0] ?? ''
              );
            },
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return (
              Object.entries(EXPORT_ENUMS).find(
                ([, val]) => val === params.value,
              )?.[0] ?? ''
            );
          },
          wrapHeaderText: true,
        },
        ...(isSemenDataPointsEnabled
          ? ([
              {
                field: 'country',
                headerName: 'Export Country',
                width: 140,
                filter: 'agTextColumnFilter',
                wrapHeaderText: true,
                filterParams: {
                  buttons: ['apply', 'clear'],
                  closeOnApply: true,
                },
              },
              {
                field: 'comment',
                headerName: 'Semen Comment',
                width: 180,
                filter: 'agTextColumnFilter',
                wrapHeaderText: true,
              },
              {
                field: 'critical',
                headerName: 'Critical',
                width: 110,
                filter: 'agNumberColumnFilter',
                wrapHeaderText: true,
              },
              {
                field: 'shortName',
                headerName: 'Short Name',
                width: 140,
                filter: 'agTextColumnFilter',
                wrapHeaderText: true,
                filterParams: {
                  buttons: ['apply', 'clear'],
                  closeOnApply: true,
                },
              },
            ] as ColDef[])
          : []),
        {
          field: 'secondaryOwner',
          headerName: 'Secondary Owner',
          width: 150,
          filter: 'agTextColumnFilter',
          wrapHeaderText: true,
        },
        ...(isSemenDataPointsEnabled
          ? isSemenTypeLabelEnabled
            ? semenDataPointColumnDefs.filter(
                (col) => col.field !== 'semenType',
              )
            : semenDataPointColumnDefs
          : []),

        {
          field: 'sortStatus',
          headerName: 'Sort Status',
          width: 120,
          wrapHeaderText: true,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [100000000],
            valueFormatter: (params: any) => {
              return params.value === 100000000 ? 'Rejected' : '';
            },
          },
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === 100000000 ? 'Rejected' : '';
          },
        },
        {
          field: 'collectionDate',
          headerName: 'Collection Date',
          width: 140,
          filter: 'agDateColumnFilter',
          wrapHeaderText: true,
          valueFormatter: formatDateMonthDayYear,
          hide: true,
        },
        {
          field: 'quantityToShip',
          headerName: 'Quantity to Ship',
          width: 120,
          filter: 'agNumberColumnFilter',
          wrapHeaderText: true,
        },
        ...(isAddStorageLocationSiteEnabled
          ? ([
              {
                field: 'semenLocationSite',
                headerName: 'Location Site',
                width: 150,
                filter: 'agTextColumnFilter',
                wrapHeaderText: true,
                filterParams: {
                  buttons: ['apply', 'clear'],
                  closeOnApply: true,
                },
              },
            ] as ColDef[])
          : []),
      ] as ColDef[],
    [
      isSemenDataPointsEnabled,
      isAddStorageLocationSiteEnabled,
      isSemenTypeLabelEnabled,
      isSemenConventionalTypeEnabled,
    ],
  );
};

export const shipmentTabSemenColumnDefs = [
  {
    headerName: '',
    headerTooltip: '',
    width: 10,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'numberOfStrawsToAdd',
    headerName: '# to add',
    width: 80,
    filter: false,
    wrapHeaderText: true,
    editable: true,
    valueParser: (params) => {
      const val = Number(params.newValue);
      return Number.isNaN(val) ? null : val;
    },
    cellClass:
      'bg-white rounded mx-0.5 max-w-12 h-[1.6rem] flex items-center justify-center semen-straws-to-add-cell',
    cellStyle: {
      borderRadius: '4px',
      margin: 'auto 2px',
      maxWidth: '3rem',
      height: '1.6rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white',
    },
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'cane',
    headerName: 'Cane Code',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'naab',
    headerName: 'NAAB Code',
    width: 140,
    filter: 'agTextColumnFilter',
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    wrapHeaderText: true,
  },
  {
    field: 'semenType',
    headerName: 'Semen Type',
    width: 140,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'sire.name',
    headerName: 'Sire Name',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'sire.breed.name',
    headerName: 'Breed',
    width: 100,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'donorScheduleDate',
    headerName: 'Donor Schedule Date',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'dateReceived',
    headerName: 'Date Received',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'stateCode',
    headerName: 'Semen Status',
    width: 130,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [0, 1],
      valueFormatter: (params: any) => {
        return params.value === 0 ? 'Active' : 'Inactive';
      },
      buttons: ['apply', 'clear'],
      closeOnApply: true,
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === 0 ? 'Active' : 'Inactive';
    },
    wrapHeaderText: true,
    hide: true,
  },
  {
    field: 'shipmentNumber',
    headerName: 'Shipment Number',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },
  {
    field: 'quantityToShip',
    headerName: 'Quantity to Ship',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'storageLocation.name',
    headerName: 'Semen Location',
    width: 140,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    filterParams: { buttons: ['apply', 'clear'], closeOnApply: true },
  },

  {
    field: 'colCode',
    headerName: 'Collection Code',
    width: 140,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },

  {
    field: 'quantityReceived',
    headerName: 'Quantity Received',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'quantityUsed',
    headerName: 'Quantity Used',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'sex',
    headerName: 'Sex',
    width: 80,
    wrapHeaderText: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [1, 2, 3],
      valueFormatter: (params: any) => {
        return params.value === 1
          ? 'Female'
          : params.value === 2
            ? 'Male'
            : params.value === 3
              ? 'Unknown'
              : 'Unknown';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === 1
        ? 'Female'
        : params.value === 2
          ? 'Male'
          : params.value === 3
            ? 'Unknown'
            : 'Unknown';
    },
  },
  {
    field: 'sexed',
    headerName: 'Sexed',
    width: 80,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [true, false],
      valueFormatter: (params: any) => {
        return params.value === true ? 'Yes' : 'No';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === true ? 'Yes' : 'No';
    },
    wrapHeaderText: true,
  },
  {
    field: 'ownerName',
    headerName: 'Owner Name',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'secondaryOwner',
    headerName: 'Secondary Owner',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },

  {
    field: 'sortStatus',
    headerName: 'Sort Status',
    width: 120,
    wrapHeaderText: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [100000000],
      valueFormatter: (params: any) => {
        return params.value === 100000000 ? 'Rejected' : '';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === 100000000 ? 'Rejected' : '';
    },
  },
  {
    field: 'collectionDate',
    headerName: 'Collection Date',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'shipmentNumber',
    headerName: 'Shipment Number',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'donorScheduleActive',
    headerName: 'Has Donor Schedule',
    width: 100,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [true, false],
      valueFormatter: (params: any) => {
        return params.value === true ? 'Yes' : 'No';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === true ? 'Yes' : 'No';
    },
  },
] as ColDef[];

export const semenTransactionColumnDefs = [
  {
    field: 'date',
    headerName: 'Date',
    width: 80,
    filter: 'agDateColumnFilter',
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'name',
    headerName: 'Description',
    width: 250,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'transactionTypeName',
    headerName: 'Transaction',
    width: 130,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    width: 80,
    filter: 'agNumberColumnFilter',
  },
  {
    field: 'sireName',
    headerName: 'Bull',
    width: 130,
    filter: 'agTextColumnFilter',
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'colCode',
    headerName: 'Collection Code',
    width: 140,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'quantityBeforeTransaction',
    headerName: 'Quantity Before Transaction',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'sex',
    headerName: 'Sex',
    width: 80,
    wrapHeaderText: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [1, 2, 3],
      valueFormatter: (params: any) => {
        return params.value === 1
          ? 'Female'
          : params.value === 2
            ? 'Male'
            : params.value === 3
              ? 'Unknown'
              : 'Unknown';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === 1
        ? 'Female'
        : params.value === 2
          ? 'Male'
          : params.value === 3
            ? 'Unknown'
            : 'Unknown';
    },
  },
  {
    field: 'sexed',
    headerName: 'Sexed',
    width: 80,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [true, false],
      valueFormatter: (params: any) => {
        return params.value === true ? 'Yes' : 'No';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === true ? 'Yes' : 'No';
    },
    wrapHeaderText: true,
  },
  {
    field: 'ownerName',
    headerName: 'Owner Name',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'secondaryOwner',
    headerName: 'Secondary Owner',
    width: 150,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },

  {
    field: 'sortStatus',
    headerName: 'Sort Status',
    width: 120,
    wrapHeaderText: true,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [100000000],
      valueFormatter: (params: any) => {
        return params.value === 100000000 ? 'Rejected' : '';
      },
    },
    valueFormatter: (params: ValueFormatterParams) => {
      return params.value === 100000000 ? 'Rejected' : '';
    },
  },
  {
    field: 'collectionDate',
    headerName: 'Collection Date',
    width: 140,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
];

export const relatedRecordsColumnDefs = [
  {
    field: 'donorId',
    headerName: 'Donor ID',
    width: 120,
    wrapHeaderText: true,
  },
  {
    field: 'matingDate',
    headerName: 'Mating Date',
    width: 140,
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'transferNbr',
    headerName: 'Transfer Nbr',
    width: 140,
    wrapHeaderText: true,
  },
];

export const fertilizationsColumnDefs = [
  {
    field: 'ivfDate',
    headerName: 'IVF Date',
    width: 150,
    filter: 'agDateColumnFilter',
    wrapHeaderText: true,
    valueFormatter: formatDateMonthDayYear,
  },
  {
    field: 'fertSite',
    headerName: 'Fertilization Site',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'donorName',
    headerName: 'Donor Name',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'sireName',
    headerName: 'Sire Name',
    width: 80,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'mating',
    headerName: 'Mating',
    width: 120,
    filter: 'agTextColumnFilter',
    wrapHeaderText: true,
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: ICellRendererParams) => ({
      fertilizationId: params.data?.fertilizationId as string,
      donorName: params.data?.mating as string,
    }),
  },
  {
    field: 'ivfNumber',
    headerName: 'IVF Number',
    width: 80,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'semenUnits',
    headerName: 'Semen Units',
    width: 80,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'transferred',
    headerName: 'Transferred',
    width: 80,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'frozen',
    headerName: 'Frozen',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
  {
    field: 'shipped',
    headerName: 'Shipped',
    width: 100,
    filter: 'agNumberColumnFilter',
    wrapHeaderText: true,
  },
] as ColDef[];

export const semenAppShipmentSideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
    },
    {
      id: 'customViews',
      labelDefault: 'Custom Views',
      labelKey: 'customViews',
      iconKey: 'custom-views',
      toolPanel: CustomViews,
      toolPanelParams: {
        setQuickFilter,
        useAppDispatch,
        useAppSelector,
      },
    },
  ],
};
